/**
 * This component will show the booking info and calculated total price.
 * I.e. dates and other details related to payment decision in receipt format.
 */
import React from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { types as sdkTypes } from '../../util/sdkLoader';

import {
  DATE_TYPE_DATE,
  DATE_TYPE_DATETIME,
  DATE_TYPE_TIME,
  LINE_ITEM_CUSTOMER_COMMISSION,
  LINE_ITEM_FIXED,
  LINE_ITEM_HOUR,
  LINE_ITEM_PROVIDER_COMMISSION,
  LISTING_UNIT_TYPES,
  propTypes,
} from '../../util/types';

import LineItemBookingPeriod from './LineItemBookingPeriod';
import LineItemBasePriceMaybe from './LineItemBasePriceMaybe';
import LineItemSubTotalMaybe from './LineItemSubTotalMaybe';
import LineItemShippingFeeMaybe from './LineItemShippingFeeMaybe';
import LineItemPickupFeeMaybe from './LineItemPickupFeeMaybe';
import LineItemCustomerCommissionMaybe from './LineItemCustomerCommissionMaybe';
import LineItemCustomerCommissionRefundMaybe from './LineItemCustomerCommissionRefundMaybe';
import LineItemProviderCommissionMaybe from './LineItemProviderCommissionMaybe';
import LineItemProviderCommissionRefundMaybe from './LineItemProviderCommissionRefundMaybe';
import LineItemRefundMaybe from './LineItemRefundMaybe';
import LineItemTotalPrice from './LineItemTotalPrice';
import LineItemUnknownItemsMaybe from './LineItemUnknownItemsMaybe';
import LineItemPetPricingMaybe from './LineItemPetPricingMaybe';

import css from './OrderBreakdown.module.css';

const { Money } = sdkTypes;

export const OrderBreakdownComponent = props => {
  const {
    rootClassName,
    className,
    userRole,
    transaction,
    fetchedLineItems,
    booking,
    timeZone,
    currency,
    marketplaceName,
    intl,
  } = props;

  const isCustomer = userRole === 'customer';
  const isProvider = userRole === 'provider';
  let allLineItems = transaction.attributes.lineItems || [];
  if (!allLineItems || allLineItems.length === 0) {
    allLineItems = fetchedLineItems;
  }
  // We'll show only line-items that are specific for the current userRole (customer vs provider)
  const rawLineItems = allLineItems.filter(lineItem => lineItem.includeFor.includes(userRole));

  const transformLineItems = items => {
    if (!isProvider) {
      return (Array.isArray(items) ? items : [])
        .filter((fi) => {
          const { code } = fi || {};
          return !['line-item/provider-commission'].includes(code);
        });
    }
    return (Array.isArray(items) ? items : [])
    .filter((fi) => {
      const { code } = fi || {};
      return !['line-item/provider-commission'].includes(code);
    })
    .map(item => {
      // Transform line item codes for provider view
      const {
        lineTotal,  
        unitPrice,
        code,
      } = item;
      const excluded_types = [
        'line-item/gratuity',
        // 'line-item/price-adjustment',
      ];
      const commissionRate = excluded_types.includes(code) ? 1 : 0.80; // 20% commission on all but tips
      const transformedLineTotal = new Money(lineTotal.amount * commissionRate, lineTotal.currency);
      const transformedUnitPrice = new Money(unitPrice.amount * commissionRate, unitPrice.currency);
      return {
        ...item,
        lineTotal: transformedLineTotal,
        unitPrice: transformedUnitPrice,
      }
    });
  }

  const lineItems = transformLineItems(rawLineItems);

  const adjustedPayoutTotal = isProvider && lineItems.length > 0
    ? lineItems.reduce((sum, item) => {
        // Both sum and item.lineTotal are Money objects
        return new Money(sum.amount + item.lineTotal.amount, currency);
      }, new Money(0, currency))
    : null;

  const adjustedPayinTotal = isCustomer && lineItems.length > 0
  ? lineItems.reduce((sum, item) => {
      return new Money(sum.amount + item.lineTotal.amount, currency);
    }, new Money(0, currency))
  : null;

  /*
  if (isCustomer) {
    console.log('=== CUSTOMER TOTAL DEBUG ===');
    console.log('Line items being summed:', lineItems.map(item => ({
      code: item.code,
      amount: item.lineTotal.amount,
      includeFor: item.includeFor
    })));
    console.log('Calculated adjustedPayinTotal:', adjustedPayinTotal?.amount);
    console.log('Transaction payinTotal:', transaction.attributes.payinTotal?.amount);
    console.log('Sum check:', lineItems.reduce((sum, item) => sum + item.lineTotal.amount, 0));
  }
  */

  // console.log('OrderBreakdown rawLineItems', rawLineItems);
  // console.log('OrderBreakdown lineItems', lineItems);
  const unitLineItem = lineItems.find(
    item => LISTING_UNIT_TYPES.includes(item.code) && !item.reversal
  );
  // Line-item code that matches with base unit: day, night, hour, fixed, item
  const lineItemUnitType = unitLineItem?.code;
  const dateType = [LINE_ITEM_HOUR, LINE_ITEM_FIXED].includes(lineItemUnitType)
    ? DATE_TYPE_DATETIME
    : DATE_TYPE_DATE;

  const hasCommissionLineItem = lineItems.find(item => {
    const hasCustomerCommission = isCustomer && item.code === LINE_ITEM_CUSTOMER_COMMISSION;
    const hasProviderCommission = isProvider && item.code === LINE_ITEM_PROVIDER_COMMISSION;
    return (hasCustomerCommission || hasProviderCommission) && !item.reversal;
  });

  const classes = classNames(rootClassName || css.root, className);

  // console.log('lineItems', isProvider, lineItems);

  /**
   * OrderBreakdown contains different line items:
   *
   * LineItemBookingPeriod: prints booking start and booking end types. Prop dateType
   * determines if the date and time or only the date is shown
   *
   * LineItemShippingFeeMaybe: prints the shipping fee (combining additional fee of
   * additional items into it).
   *
   * LineItemShippingFeeRefundMaybe: prints the amount of refunded shipping fee
   *
   * LineItemBasePriceMaybe: prints the base price calculation for the listing, e.g.
   * "$150.00 * 2 nights $300"
   *
   * LineItemUnknownItemsMaybe: prints the line items that are unknown. In ideal case there
   * should not be unknown line items. If you are using custom pricing, you should create
   * new custom line items if you need them.
   *
   * LineItemSubTotalMaybe: prints subtotal of line items before possible
   * commission or refunds
   *
   * LineItemRefundMaybe: prints the amount of refund
   *
   * LineItemCustomerCommissionMaybe: prints the amount of customer commission
   * The default transaction process used by this template doesn't include the customer commission.
   *
   * LineItemCustomerCommissionRefundMaybe: prints the amount of refunded customer commission
   *
   * LineItemProviderCommissionMaybe: prints the amount of provider commission
   *
   * LineItemProviderCommissionRefundMaybe: prints the amount of refunded provider commission
   *
   * LineItemTotalPrice: prints total price of the transaction
   *
   */

  return (
    <div className={classes}>
      <LineItemBookingPeriod
        booking={booking}
        transaction={transaction} 
        code={lineItemUnitType}
        dateType={dateType}
        timeZone={timeZone}
      />

      {/*
      <LineItemBasePriceMaybe lineItems={lineItems} code={lineItemUnitType} intl={intl} />
      <LineItemShippingFeeMaybe lineItems={lineItems} intl={intl} />
      <LineItemPickupFeeMaybe lineItems={lineItems} intl={intl} />
      */}
      <LineItemPetPricingMaybe lineItems={lineItems} isProvider={isProvider} intl={intl} />
      <LineItemUnknownItemsMaybe lineItems={lineItems} isProvider={isProvider} intl={intl} transaction={transaction} />

      <LineItemSubTotalMaybe
        lineItems={lineItems}
        code={lineItemUnitType}
        userRole={userRole}
        intl={intl}
        marketplaceCurrency={currency}
      />
      <LineItemRefundMaybe lineItems={lineItems} intl={intl} marketplaceCurrency={currency} />

      <LineItemCustomerCommissionMaybe
        lineItems={lineItems}
        isCustomer={isCustomer}
        marketplaceName={marketplaceName}
        intl={intl}
      />
      <LineItemCustomerCommissionRefundMaybe
        lineItems={lineItems}
        isCustomer={isCustomer}
        marketplaceName={marketplaceName}
        intl={intl}
      />

      <LineItemProviderCommissionMaybe
        lineItems={lineItems}
        isProvider={isProvider}
        marketplaceName={marketplaceName}
        intl={intl}
      />
      <LineItemProviderCommissionRefundMaybe
        lineItems={lineItems}
        isProvider={isProvider}
        marketplaceName={marketplaceName}
        intl={intl}
      />

      <LineItemTotalPrice
        transaction={transaction}
        isProvider={isProvider}
        intl={intl}
        adjustedPayoutTotal={adjustedPayoutTotal}
        adjustedPayinTotal={adjustedPayinTotal}
      />

      {hasCommissionLineItem ? (
        <span className={css.feeInfo}>
          <FormattedMessage id="OrderBreakdown.commissionFeeNote" />
        </span>
      ) : null}
    </div>
  );
};

/**
 * Order breakdown aka receipt
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string} props.marketplaceName
 * @param {string} props.timeZone IANA time zone name
 * @param {string} props.currency E.g. 'USD'
 * @param {'customer' | 'provider'} props.userRole
 * @param {propTypes.transaction} props.transaction
 * @param {propTypes.booking?} props.booking
 * @param {DATE_TYPE_DATE | DATE_TYPE_TIME | DATE_TYPE_DATETIME} props.dateType
 * @returns {JSX.Element} the order breakdown component
 */
const OrderBreakdown = props => {
  const intl = useIntl();
  return <OrderBreakdownComponent intl={intl} {...props} />;
};

export default OrderBreakdown;
